<template>
  <header v-if="showHeader" id="header">
    <div class="left-buttons">
      <button
        type="button"
        class="hamburger-button"
        :aria-label="$t('globalSideMenu.ariaOpen')"
        @click="onClickHamburger"
      >
        <img class="hamburger-icon" :src="hamburgerIcon" alt="" />
        <span v-if="showDesktopAppNotice" class="notice-dot" aria-hidden="true"></span>
      </button>
      <router-link class="logo" to="/">
        <img class="logo-img" src="@/assets/img/logo.png" />
      </router-link>
      <div class="links">
        <router-link
          class="link-item"
          :class="{ active: isActivePath('/home') }"
          to="/"
          @click.native="onClickLink('ホーム')"
        >
          {{ $t("header.nav.home") }}
        </router-link>
        <router-link
          class="link-item"
          :class="{ active: isActivePath('/nolanovel') }"
          to="/nolanovel"
          @click.native="onClickLink('作品投稿')"
        >
          {{ $t("header.nav.novelPosting") }}
        </router-link>
        <router-link
          class="link-item tutorial-agent"
          :class="{ active: isActivePath('/agent') }"
          to="/agent"
          @click.native="onClickLink('エージェント')"
        >
          {{ $t("header.nav.agent") }}
        </router-link>
        <router-link
          class="link-item"
          :class="{ active: isActivePath('/event/contest') }"
          to="/event/contest"
          @click.native="onClickLink('コンテスト')"
        >
          {{ $t("header.nav.contest") }}
        </router-link>
        <router-link
          class="link-item"
          :class="{ active: isActivePath('/event', '/event/contest') }"
          to="/event/company"
          @click.native="onClickLink('商業募集')"
        >
          {{ $t("header.nav.commercial") }}
        </router-link>
        <router-link
          class="link-item"
          :class="{ active: isActivePath('/knowhow') }"
          to="/knowhow"
          @click.native="onClickLink('学習')"
        >
          {{ $t("header.nav.learning") }}
        </router-link>
        <router-link
          class="link-item"
          :class="{ active: isActivePath('/assistant-cat') }"
          :to="{ name: 'assistantCatHome' }"
          @click.native="onClickAssistantCatHome"
        >
          {{ $t("header.nav.assistantCat") }}
        </router-link>
      </div>
    </div>
    <div class="right-buttons">
      <image-icon
        v-if="isNolanovelOrAgentPath"
        class="icon-item beginners-icon"
        :to="{ name: 'commerceDebutGuide' }"
        :src="require('@/assets/img/icon/beginners-icon.webp')"
      />
      <router-link class="icon-item notification-icon" :class="{ unread: isUnread }" to="/news">
        <span class="header-icon-wrapper">
          <img :src="require('@/assets/icons/notification.svg')" height="18px" />
        </span>
      </router-link>
      <router-link
        class="icon-item inbox-icon"
        :class="{ unread: hasUnreadInbox }"
        to="/inbox"
        @click.native="onClickInbox"
      >
        <span class="header-icon-wrapper">
          <img :src="require('@/assets/icons/inbox.svg')" height="18px" />
        </span>
      </router-link>
      <router-link class="icon-item mypage-icon" :to="{ name: 'myPage' }">
        <span class="header-icon-wrapper">
          <img :src="require('@/assets/icons/mypage.svg')" height="18px" />
        </span>
      </router-link>
    </div>
    <global-side-menu :is-open="isSideMenuOpen" @close="onCloseSideMenu" />
  </header>
</template>

<script lang="ts">
import Vue from "vue";
import { Auth } from "aws-amplify";
import ImageIcon from "@/components/atoms/ImageIcon.vue";
import GlobalSideMenu from "@/components/organisms/GlobalSideMenu.vue";
import HamburgerIcon from "@/assets/icons/figma/Hamburger.svg";
import { News } from "@/lib/models/general";
import { NotificationsFromNolaNovel } from "@/lib/models";
import { NolaAnalytics, NolaItemId } from "@/lib/utils/analytics";
import { isMobile } from "@/constants/breakpoints";

export default Vue.extend<Data, Methods, Computed, {}>({
  components: { ImageIcon, GlobalSideMenu },
  data() {
    return {
      isLoggedIn: null as boolean | null,
      isSideMenuOpen: false,
    };
  },
  async created() {
    try {
      const user = (await Auth.currentCredentials()) as any;
      this.isLoggedIn = user && !user.code && user.authenticated;
    } catch {
      this.isLoggedIn = false;
    }
  },
  computed: {
    showHeader() {
      const authType = this.$route.meta?.authType;
      // 認証必須ページの場合、ルーターガードが認証済みを保証するため常に表示
      if (authType === "require_auth") return true;
      // 認証不要ページ（authType: null）の場合、ログイン状態に基づいて表示
      return this.isLoggedIn === true;
    },
    isActivePath() {
      return (name, excludePath) => {
        const { path } = this.$route;
        if (excludePath && path.startsWith(excludePath)) {
          return false;
        }
        return path.startsWith(name);
      };
    },
    isUnread() {
      const { latestNews, readLatestNews } = this;
      if (latestNews && readLatestNews) {
        return latestNews.updatedAt !== readLatestNews.updatedAt;
      }
      return true;
    },
    latestNews() {
      return this.$store.getters["generalModule/latestNews"];
    },
    readLatestNews() {
      return this.$store.getters["generalModule/readLatestNews"];
    },
    isUnreadFromNolaNovel() {
      const unreadNotifications = this.$store.getters["nolaNovelModule/notifications"](
        false
      ) as NotificationsFromNolaNovel;
      return unreadNotifications.items.length > 0;
    },
    hasUnreadAgentNotifications() {
      const notificationSummary = this.$store.getters["notificationModule/notificationSummary"];
      return notificationSummary?.unreadItemCount > 0;
    },
    hasUnreadInbox() {
      // エージェント通知またはNolaノベル通知に未読があれば表示
      return this.hasUnreadAgentNotifications || this.isUnreadFromNolaNovel;
    },
    isNolanovelOrAgentPath() {
      const { path } = this.$route;
      return path.startsWith("/nolanovel") || path.startsWith("/agent");
    },
    hamburgerIcon() {
      return HamburgerIcon;
    },
    isFreePlan() {
      return this.$store.getters["billingModule/isFreePlan"];
    },
    showDesktopAppNotice() {
      return !this.isFreePlan && !this.$store.getters["generalModule/hasSeenDesktopAppOpenBetaInSideMenu"];
    },
  },
  methods: {
    onClickLink(name) {
      this.$gtm.trackEvent({
        event: "click_header_link",
        value: name,
      });
    },
    onClickInbox() {
      const nolaAnalytics = new NolaAnalytics(this.$gtm, this.$store);
      nolaAnalytics.logButtonEvent(NolaItemId.HeaderInboxClick);
    },
    onClickAssistantCatHome() {
      this.onClickLink("猫ホーム");
      const nolaAnalytics = new NolaAnalytics(this.$gtm, this.$store);
      nolaAnalytics.logButtonEvent(NolaItemId.AssistantCatHomeTopMenuButton);
    },
    onClickHamburger() {
      this.isSideMenuOpen = true;
      if (this.showDesktopAppNotice && !isMobile()) {
        this.$store.dispatch("generalModule/setSeenDesktopAppOpenBetaInSideMenu", true);
      }
    },
    onCloseSideMenu() {
      this.isSideMenuOpen = false;
    },
  },
});

interface Data {
  isLoggedIn: boolean | null;
  isSideMenuOpen: boolean;
}

interface Computed {
  showHeader: boolean;
  isActivePath(name: string, excludePath?: string): boolean;
  isUnread: boolean;
  latestNews: News;
  readLatestNews: News;
  isUnreadFromNolaNovel: boolean;
  hasUnreadAgentNotifications: boolean;
  hasUnreadInbox: boolean;
  isNolanovelOrAgentPath: boolean;
  hamburgerIcon: string;
  isFreePlan: boolean;
  showDesktopAppNotice: boolean;
}

interface Methods {
  onClickLink(name: string): void;
  onClickInbox(): void;
  onClickAssistantCatHome(): void;
  onClickHamburger(): void;
  onCloseSideMenu(): void;
}
</script>
<style lang="scss" scoped>
$header-border: 2px;

#header {
  display: flex;
  position: sticky;
  align-items: center;
  justify-content: space-between;
  background-color: var(--color-surface-muted);
  border-bottom: var(--color-border) $header-border solid;
  height: $header-height;
  line-height: 1rem;
  box-sizing: border-box;
  left: 0;
  right: 0;
  top: 0;
  z-index: 20;
  padding: 0 15px;

  @include mq(sp) {
    height: 50px;
  }

  .left-buttons {
    height: 100%;
    display: flex;
    align-items: center;

    .hamburger-button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      border: none;
      background: transparent;
      cursor: pointer;
      border-radius: var(--radius-sm);
      position: relative;
      @include hover-overlay;

      .hamburger-icon {
        width: 24px;
        height: 24px;
        filter: brightness(0);

        [data-app-theme="dark"] & {
          filter: brightness(0) invert(1);
        }
      }

      .notice-dot {
        position: absolute;
        top: 6px;
        right: 6px;
        z-index: 1;
        width: 6px;
        height: 6px;
        border-radius: var(--radius-circle);
        background-color: var(--color-danger);
        border: 2px solid var(--color-surface-muted);
        pointer-events: none;

        @include mq(sp) {
          display: none;
        }
      }
    }

    .links {
      display: flex;
      margin: 8px;

      @include mq(sp) {
        display: none;
      }

      .link-item {
        position: relative;
        box-sizing: border-box;
        padding: 4px;
        margin: 0 10px;
        color: var(--color-text-normal);
        font-size: var(--font-size-md);
        font-weight: var(--font-weight-bold);
        text-decoration: none;

        &.active {
          border-bottom: 2px solid var(--color-primary);
        }
      }
    }
  }

  .right-buttons {
    height: 100%;
    display: flex;
    align-items: center;

    .icon-item {
      display: flex;
      margin: 0 10px;
    }

    .notification-icon,
    .inbox-icon {
      position: relative;

      &.unread::after {
        position: absolute;
        top: -2px;
        right: -2px;
        content: "";
        width: 6px;
        height: 6px;
        border-radius: var(--radius-circle);
        background-color: #e87474;
        border: 2px solid #f3f3f2;
      }
    }

    .header-icon-wrapper {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      background-color: rgba(0, 0, 0, 0.12);
      border-radius: var(--radius-circle);

      [data-app-theme="dark"] & {
        background-color: rgba(255, 255, 255, 0.12);
      }

      img {
        filter: brightness(0);
        transition: filter 0.3s;

        [data-app-theme="dark"] & {
          filter: brightness(0) invert(1);
        }
      }
    }

    @include mq(sp) {
      right: 8px;
    }
  }
}

.back-to-top {
  color: var(--color-text-normal);
  font-size: smaller;
  padding: 5px;
  cursor: pointer;
}

.logo {
  display: block;
  margin: 0 30px;

  @include mq(sp) {
    margin: 0 0 0 8px;
  }
}

.logo-img {
  margin: 0 auto;
  height: 30px;

  [data-app-theme="dark"] & {
    filter: brightness(0) invert(0.9);
  }
}

.icon-mypage {
  flex-grow: 0;
}
</style>
